import GameCore from "../GameCore";
import DamageNumberGroup from "./DamageNumberGroup";
import { GAME_FONTS } from "../../../utils/FontManager";
import Game from "../Game";

export default class DamageNumber extends Phaser.GameObjects.Text {
  isCullable: boolean;
  isTeleportOnCull: boolean;
  owner: null;
  pool: DamageNumberGroup;
  tween2: Phaser.Tweens.Tween | null;
  tween1: Phaser.Tweens.Tween | null;

  // Add failsafe timer as instance property
  private failsafeTimer: NodeJS.Timeout | null;

  constructor(group: DamageNumberGroup, x: number, y: number) {
    super(group.scene, x, y, "", {
      fontFamily: GAME_FONTS.PIXEL,
      stroke: "#000000",
      strokeThickness: 3,
    });

    // Ensure the object is properly added to the scene first
    this.scene.add.existing(this);

    // Initialize properties
    this.isCullable = true;
    this.isTeleportOnCull = false;
    this.owner = null;
    this.pool = group;
    this.tween1 = null;
    this.failsafeTimer = null;

    // Create the initial tween - but ensure 'this' is fully initialized
    try {
      this.tween2 = this.scene.tweens.add({
        targets: this,
        alpha: 0,
        scale: GameCore.PixelScale,
        duration: 400,
        paused: true,
        onComplete: () => {
          this.DeSpawn();
        },
      });
    } catch (error) {
      this.tween2 = null;
    }
  }

  OnTeleportOnCull() {
    // No-op for culling compatibility
  }

  Init() {
    this.setDepth(GameCore.ZDamageNumber);
    this.setOrigin(0.5, 1);
  }

  OnRecycle(x: number, y: number, PPower: number) {
    const random = Math.random() - 0.5;
    const damage = 10 * (PPower + random);

    // Get current number of damage numbers to adjust spread
    let spreadMultiplier = 1;
    try {
      if (Game.Core && Game.Core.damageNumberPool) {
        const poolStats = Game.Core.damageNumberPool.getStats();
        // Increase spread when there are many damage numbers
        spreadMultiplier = Math.min(1 + poolStats.spawned / 10, 2);
      }
    } catch (error) {
      // Use default spread
    }

    // Ensure proper initialization
    this.setVisible(damage >= 1);
    this.setAlpha(1);
    this.setScale(GameCore.PixelScale);
    this.setPosition(x + 32 * spreadMultiplier * random, y - 16);
    this.setText(damage.toFixed());
    this.setColor("#ffffff");

    // Set color based on power
    if (PPower >= 6) {
      this.setColor("#FF4500");
    } else if (PPower >= 4) {
      this.setColor("#FA8072");
    } else if (PPower >= 3) {
      this.setColor("#FFDAB9");
    }

    // Clean up existing tweens
    if (this.tween1) {
      this.tween1.stop();
      this.tween1 = null;
    }
    if (this.tween2) {
      this.tween2.stop();
      this.tween2 = null;
    }

    // Only animate if visible and poolManager exists
    if (this.visible && Game.Core.poolManager) {
      // Clear any existing failsafe timer before creating a new one
      if (this.failsafeTimer) {
        clearTimeout(this.failsafeTimer);
        this.failsafeTimer = null;
      }

      // Add a simple failsafe timeout (2x the expected animation duration)
      this.failsafeTimer = setTimeout(() => {
        console.warn(`[DamageNumber] Failsafe timer triggered after 4s`);
        this.DeSpawn();
      }, 4000);

      try {
        Game.Core.poolManager.animateDamageNumber(this, this.y, PPower, () => {
          if (this.failsafeTimer) {
            clearTimeout(this.failsafeTimer);
            this.failsafeTimer = null;
          }
          this.DeSpawn();
        });
      } catch (error) {
        if (this.failsafeTimer) {
          clearTimeout(this.failsafeTimer);
          this.failsafeTimer = null;
        }
        this.DeSpawn();
      }
    } else {
      // If not visible or no poolManager, just despawn immediately
      this.DeSpawn();
    }
  }

  DeSpawn() {
    // Prevent double despawn
    if ((this as any)._isReturning) {
      return;
    }
    (this as any)._isReturning = true;

    // Check if object is still valid
    if (!this.scene || !this.pool) {
      (this as any)._isReturning = false;
      return;
    }

    // Clean up any remaining tweens before returning to pool
    if (this.tween1) {
      try {
        this.tween1.stop();
        this.tween1 = null;
      } catch (error) {
        console.warn(`[DamageNumber] Error stopping tween1:`, error);
      }
    }

    if (this.tween2) {
      try {
        this.tween2.stop();
        this.tween2 = null;
      } catch (error) {
        console.warn(`[DamageNumber] Error stopping tween2:`, error);
      }
    }

    // Clean up any remaining failsafe timer
    if (this.failsafeTimer) {
      clearTimeout(this.failsafeTimer);
      this.failsafeTimer = null;
    }

    try {
      this.pool.Return(this);
    } catch (error) {
      console.error(`[DamageNumber] Error returning to pool:`, error);
    }
  }
}
